/**
 * Lightweight game sound effects, synthesized with the Web Audio API.
 *
 * No audio files are shipped or fetched — every effect is generated from
 * oscillators at play time. This keeps the offline app self-contained (nothing
 * for the service worker to cache) and adds zero network weight.
 *
 * The learner's mute preference is persisted in localStorage so it survives
 * across games and reloads. Audio is created lazily on first play because
 * browsers require a user gesture before an AudioContext can start.
 */

const MUTE_KEY = 'vn-sound-muted';

let ctx: AudioContext | null = null;

/** Lazily create (and resume) the shared AudioContext. */
function audioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  if (!ctx) {
    try {
      ctx = new Ctor();
    } catch {
      return null;
    }
  }
  // Autoplay policies suspend the context until a gesture — resume on demand.
  if (ctx.state === 'suspended') void ctx.resume();
  return ctx;
}

export function isSoundMuted(): boolean {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(MUTE_KEY) === '1';
}

export function setSoundMuted(muted: boolean): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(MUTE_KEY, muted ? '1' : '0');
}

/** Flip the mute flag and return the new value. */
export function toggleSoundMuted(): boolean {
  const next = !isSoundMuted();
  setSoundMuted(next);
  return next;
}

interface ToneStep {
  /** Frequency in Hz. */
  freq: number;
  /** Start offset from the sound's beginning, in seconds. */
  at: number;
  /** Duration in seconds. */
  dur: number;
  /** Oscillator shape. */
  type?: OscillatorType;
  /** Peak gain (0–1). */
  gain?: number;
}

/** Play a sequence of short tones on the shared context. */
function playTones(steps: ToneStep[]): void {
  if (isSoundMuted()) return;
  const context = audioContext();
  if (!context) return;
  const now = context.currentTime;
  for (const step of steps) {
    const osc = context.createOscillator();
    const gain = context.createGain();
    osc.type = step.type ?? 'sine';
    osc.frequency.value = step.freq;
    const start = now + step.at;
    const peak = step.gain ?? 0.14;
    // Quick attack, smooth exponential decay — reads as a soft "blip", not a beep.
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(peak, start + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + step.dur);
    osc.connect(gain).connect(context.destination);
    osc.start(start);
    osc.stop(start + step.dur + 0.02);
  }
}

/** Musical note frequencies used by the cues below. */
const NOTE = {
  C5: 523.25,
  E5: 659.25,
  G5: 783.99,
  C6: 1046.5,
  E6: 1318.51,
  A4: 440,
  Fs4: 369.99,
  D4: 293.66,
} as const;

/** Correct answer: a bright rising two-note major third. */
export function playCorrect(): void {
  playTones([
    { freq: NOTE.E5, at: 0, dur: 0.12, type: 'triangle', gain: 0.13 },
    { freq: NOTE.G5, at: 0.09, dur: 0.16, type: 'triangle', gain: 0.13 },
  ]);
}

/** Wrong answer: a soft descending buzz — noticeable but not harsh. */
export function playWrong(): void {
  playTones([
    { freq: NOTE.Fs4, at: 0, dur: 0.14, type: 'sawtooth', gain: 0.08 },
    { freq: NOTE.D4, at: 0.1, dur: 0.18, type: 'sawtooth', gain: 0.08 },
  ]);
}

/** UI tap / chip placement: a very short neutral click. */
export function playTap(): void {
  playTones([{ freq: NOTE.A4, at: 0, dur: 0.05, type: 'square', gain: 0.06 }]);
}

/** Countdown tick when a timed game is running low. */
export function playTick(): void {
  playTones([{ freq: NOTE.C5, at: 0, dur: 0.05, type: 'sine', gain: 0.08 }]);
}

/** Round / game win: a cheerful ascending arpeggio. */
export function playWin(): void {
  playTones([
    { freq: NOTE.C5, at: 0, dur: 0.14, type: 'triangle', gain: 0.14 },
    { freq: NOTE.E5, at: 0.11, dur: 0.14, type: 'triangle', gain: 0.14 },
    { freq: NOTE.G5, at: 0.22, dur: 0.14, type: 'triangle', gain: 0.14 },
    { freq: NOTE.C6, at: 0.33, dur: 0.24, type: 'triangle', gain: 0.15 },
  ]);
}

/** Losing / out of lives: a low two-note fall. */
export function playLose(): void {
  playTones([
    { freq: NOTE.D4, at: 0, dur: 0.18, type: 'sine', gain: 0.1 },
    { freq: NOTE.A4 / 2, at: 0.14, dur: 0.28, type: 'sine', gain: 0.1 },
  ]);
}

/** Streak / combo milestone: a quick sparkle above the correct cue. */
export function playStreak(): void {
  playTones([
    { freq: NOTE.G5, at: 0, dur: 0.1, type: 'triangle', gain: 0.12 },
    { freq: NOTE.C6, at: 0.08, dur: 0.1, type: 'triangle', gain: 0.12 },
    { freq: NOTE.E6, at: 0.16, dur: 0.16, type: 'triangle', gain: 0.13 },
  ]);
}

/** Advancing to the next lesson step. */
export function playStep(): void {
  playTones([
    { freq: NOTE.C5, at: 0, dur: 0.08, type: 'sine', gain: 0.1 },
    { freq: NOTE.G5, at: 0.06, dur: 0.12, type: 'sine', gain: 0.1 },
  ]);
}
